import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Factory, Recycle, Zap, Settings, Wrench } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import waterim from './images1/water.png';

const services = [
  {
    title: 'Sewage Treatment Plants (STP)',
    description: 'RENVITEK designs and implements high-performance Sewage Treatment Plants for urban and rural applications, enabling the conversion of sewage into reusable water suitable for non-potable applications. Our STPs are engineered for process stability, Robust Equipment, low energy demand, Odor-free operation, and minimal chemical dependency. We execute STPs across a wide capacity range, from 100 KLD to 30 MLD, serving residential complexes, commercial buildings, ndustries, hotels, hospitals, and institutional facilities. Each system is customized based on influent characteristics, discharge norms, and reuse objectives.',
    features: ['Activated Sludge Process (ASP)', 'Effective organic & suspended load removal', 'Statutory compliance'],
    icon: Droplets
  },
  {
    title: 'Effluent Treatment Plants (ETP)',
    description: 'Our Effluent Treatment Plants are engineered to treat complex industrial wastewater streams containing high organic loads, dissolved solids, and contaminants. RENVITEK integrates biological, physico-chemical, and advanced embrane-based processes to ensure compliance with environmental discharge and reuse norms.',
    features: ['Advanced biological treatment', 'Ultra-filtration & nano-filtration', 'Regulatory-compliant effluent'],
    icon: Factory
  },
  {
    title: 'Reverse Osmosis (RO) Plants',
    description: 'RENVITEK supplies custom-engineered Reverse Osmosis systems for potable, process, and high-purity water applications. Our RO plants are designed for high recovery, consistent output quality, and reduced water rejection, making them suitable for industrial, commercial, and residential applications.',
    features: ['High-efficiency membranes', 'Effective TDS reduction', 'Modular & scalable design'],
    icon: Recycle
  }
];

export const WaterSolutionsPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="water-solutions-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Water Treatment
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Integrated Water & Wastewater Solutions
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto">
              End-to-end water and wastewater treatment solutions for residential, commercial, 
              institutional, and industrial sectors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-4">
              Our Water Treatment Services
            </h2>
            <p className="text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              RENVITEK ENGINEERS LLP is a multidisciplinary engineering organization delivering end to-end water and wastewater treatment solutions for residential, commercial, institutional, and industrial sectors. Based in Hyderabad, we specialize in the engineering, supply, installation, commissioning, and lifecycle support of advanced water treatment systems designed to achieve regulatory compliance, operational reliability, and sustainable water management.Our solutions are engineered to address the complete water cycle—from raw water treatment to wastewater recycling, resource recovery, and zero liquid discharge—ensuring optimized performance and long-term operational efficiency.

            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-slate-50 dark:bg-emerald-900/50 p-8 hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <service.icon className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mb-4" />
                <h3 className="text-xl font-bold text-emerald-900 dark:text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600 dark:text-white/70 mb-4 text-sm leading-relaxed tracking-wide">
                  {service.description}
                </p>
                <ul className="space-y-2 tracking-wider leading-relaxed">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <span className="text-amber-500 mt-1">•</span>
                      <span className="text-slate-600 dark:text-white/70">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Scope of Services */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                Scope of Services
              </h2>
              <p className="text-slate-600 dark:text-white/70 mb-8 tracking-wider leading-relaxed">
                Our solutions are engineered to address the complete water cycle—from raw water 
                treatment to wastewater recycling, resource recovery, and zero liquid discharge—
                ensuring optimized performance and long-term operational efficiency.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {['Design', 'Detailed Engineering', 'Supply', 'Installation', 'Commissioning', 'O&M Support'].map((item) => (
                  <div key={item} className="bg-white dark:bg-emerald-900 p-4 flex items-center gap-3 tracking-wider leading-relaxed">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                    <span className="text-emerald-900 dark:text-white font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src={waterim}
                alt="Water Treatment Plant"
                className="w-full h-auto rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Chemicals & Spares */}
      <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-4">
              Supply of Chemicals & Spares
            </h2>
            <p className="text-slate-600 dark:text-white/70 tracking-wider leading-relaxed  ">
              RENVITEK supplies a comprehensive range of water treatment chemicals and critical spares, ensuring uninterrupted plant operation.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
          >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 tracking-wider leading-relaxed">
              {[
                'Sodium Hypochlorite',
                'Alum',
                'Poly Aluminium Chloride (PAC)',
                'Sodium Chloride (Salt)',
                'Caustic Soda (NaOH)',
                'RO Membranes',
                'Pumps',
                'Filter Media',
                'Cartridges',
                'Pressure Vessels'
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 bg-white dark:bg-emerald-900 p-4">
                  <div className="w-2 h-2 bg-amber-500 rounded-full" />
                  <span className="text-slate-700 dark:text-white">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Water Softener Systems */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">Water Softener Systems</h2>
          
<div className="mb-8">
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">Our Water Softener Plants are designed to remove hardness-causing minerals such as calcium and magnesium, protecting downstream equipment and improving water usability. Systems are available in manual and fully automatic configurations, suitable for domestic, Commercial, and industrial applications.</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed"><b>Benefits:</b>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">
              <li>Improved equipment life</li>
              <li>Reduced scaling and fouling</li>
              <li>Optimized water quality for process and utility use</li>
            </ul></p>
            </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Mineral Water Plants
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 leading-relaxed tracking-wider">RENVITEK offers Mineral Water Plant solutions engineered to produce safe, palatable, and high-quality drinking water conforming to applicable standards. Our systems integrate filtration, purification, and disinfection technologies, ensuring consistent quality and operational reliability.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Leachate Treatment Plants (LTP)
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">Landfill leachate presents significant environmental challenges due to high organic load and toxic contaminants. RENVITEK’s Leachate Treatment Plants are designed using robust multi-stage treatment processes to achieve safe disposal or reuse.</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed"><b>Key Features:</b>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>High-strength contaminant removal</li>
              <li>Advanced treatment configurations</li>
              <li>Environmentally safe treated water output</li>
            </ul></p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Zero Liquid Discharge (ZLD) Systems
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">RENVITEK delivers Zero Liquid Discharge solutions for industries aiming to eliminate wastewater discharge entirely. Our ZLD systems maximize water ecovery while converting residual waste into solid form for safe disposal.</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed"><b>Key Features:</b>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>Near-total water recovery</li>
              <li>Elimination of liquid effluent</li>
              <li>Sustainable and regulation-compliant water management</li>
            </ul></p>
          </div>
        </motion.div>
        </div>
      </section>
      {/* Thermal & Advanced Treatment Systems */}
      <section className="py-12 md:py-10 bg-white-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">Thermal & Advanced Treatment Systems</h2>
          
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Multiple Effect Evaporators (MEE)
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">MEEs are employed for concentration of dissolved salts and wastewater streams through multi-stage evaporation under reduced pressure, enabling efficient recovery and reduced operating costs.</p>
            </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Stripper Columns
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">Designed for the removal of volatile organic compounds (VOCs) from industrial effluents using controlled steam stripping techniques.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Agitated Thin Film Dryers (ATFD)
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">ATFD systems enable efficient drying and solidification of concentrated effluents, particularly suitable for heat-sensitive and viscous materials.</p>
          </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Operation & Maintenance (O&M)
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">RENVITEK provides comprehensive O&M services to ensure sustained performance of water and wastewater treatment facilities. Our services include preventive maintenance, process optimization, performance audits, and calibration.</p>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed"><b>Benefits:</b>
            <ul className="list-disc pl-6 space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
              <li>Improved operational efficiency</li>
              <li>Reduced downtime and operating costs</li>
              <li>Extended equipment lifespan</li>
            </ul></p>
          </div>
        </motion.div>
        </div>
      </section>
      {/* Thermal & Advanced Treatment Systems */}
      <section className="py-12 md:py-10 bg-slate-50 dark:bg-emerald-900/30">
        <div className="w-full px-4 sm:px-6 lg:px-12">
        <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6 text-center">Troubleshooting & Performance Optimization</h2>
          
          <div className="mb-8">
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">Our technical experts offer root-cause analysis and corrective action services for underperforming treatment systems. Through data evaluation and site diagnostics, we ensure rapid issue resolution and process stabilization.</p>
            </div>
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
             Refurbishment & System Upgradation
            </h2>
            <p className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">We provide cost-effective refurbishment solutions to enhance the performance of existing treatment plants, enabling improved efficiency without complete system replacement.</p>
          </div>
        </motion.div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-emerald-950 mb-4">
            Need Water Treatment Solutions?
          </h2>
          <p className="text-emerald-900 text-lg mb-8 tracking-wider leading-relaxed">
            Contact us to discuss your water and wastewater treatment requirements.
          </p>
          <Link to="/contact">
            <motion.button
              className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold inline-flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="water-cta-btn"
            >
              Get in Touch
              <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WaterSolutionsPage;
